import React, { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { toast } from "sonner";
import { brl, fmtDate } from "../lib/format";
import {
  Copy, Receipt, Image as ImageIcon, X, VideoCamera, Warning, CheckCircle,
  UploadSimple, Camera, PaperPlaneTilt, Clock, Prohibit, LinkSimple,
} from "@phosphor-icons/react";

const PIX_TYPE_LABEL = {
  cpf: "CPF", cnpj: "CNPJ", email: "E-mail", telefone: "Telefone", aleatoria: "Chave aleatória",
};

const RECEIPT_STATUS = {
  submitted: { label: "Aguardando confirmação", icon: Clock, bg: "bg-brand-yellow", text: "text-brand-ink" },
  confirmed: { label: "Confirmado", icon: CheckCircle, bg: "bg-brand-success", text: "text-white" },
  rejected: { label: "Rejeitado", icon: Prohibit, bg: "bg-brand-peach", text: "text-brand-ink" },
};

export default function MyPendingLives() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [uploadFor, setUploadFor] = useState(null); // {live_id, live_name, total}
  const [requesting, setRequesting] = useState(null); // live_id currently sending request

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/pending-lives", { params: { _: Date.now() } });
      setGroups(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar pendências");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grandTotal = groups.reduce((s, g) => s + (g.total || 0), 0);
  const itemsCount = groups.reduce((s, g) => s + (g.orders_count || 0), 0);

  const copy = async (text, label = "Chave Pix") => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiada!`);
    } catch { toast.error("Não foi possível copiar"); }
  };

  const requestLink = async (g) => {
    if (requesting) return;
    setRequesting(g.live_id);
    try {
      await api.post(`/me/pending-lives/${g.live_id}/link-requests`, {
        amount: g.total,
        note: null,
      });
      toast.success("Solicitação enviada! A loja vai gerar o link e mandar no seu direct.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao solicitar");
    } finally { setRequesting(null); }
  };

  const cancelLinkRequest = async (rid) => {
    if (!window.confirm("Cancelar esta solicitação de link?")) return;
    try {
      await api.delete(`/me/link-requests/${rid}`);
      toast.success("Solicitação cancelada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cancelar");
    }
  };

  return (
    <div data-testid="my-pending-lives-page" className="space-y-6">
      <header>
        <p className="label-brutal">Financeiro</p>
        <h1 className="font-heading text-3xl sm:text-5xl font-black">Lives a pagar</h1>
        <p className="text-brand-ink2 mt-2">
          Seus arremates que ainda <strong>não estão marcados como pagos</strong>, agrupados por live.
          Pague pelo QR Code ou chave Pix e envie o comprovante direto por aqui.
        </p>
      </header>

      {loading ? (
        <div className="card-brutal p-10 text-center font-bold">Carregando…</div>
      ) : groups.length === 0 ? (
        <div className="card-brutal p-10 text-center bg-brand-mint">
          <CheckCircle weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Nada pendente por aqui!</p>
          <p className="text-brand-ink2 text-sm">Tudo em dia — sem lives com pagamento em aberto.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Lives com pendência" value={groups.length} bg="bg-brand-yellow" icon={VideoCamera} />
            <Stat label="Itens pendentes" value={itemsCount} bg="bg-brand-peach" icon={Receipt} />
            <Stat label="Total em aberto" value={brl(grandTotal)} bg="bg-brand-pink text-white" icon={Warning} />
          </div>

          <div className="space-y-4">
            {groups.map(g => (
              <div key={g.live_id || "sem-live"} data-testid={`pending-live-${g.live_id}`} className="card-brutal overflow-hidden">
                <div className="flex flex-wrap items-start justify-between gap-3 bg-brand-mint px-4 py-3 border-b-2 border-brand-ink">
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-brand-ink2">
                      {g.live_date ? fmtDate(g.live_date) : "Sem data"}
                      {g.contractor_name ? <> · {g.contractor_name}</> : null}
                    </p>
                    <p className="font-heading text-lg sm:text-2xl font-black break-words">{g.live_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] font-bold uppercase text-brand-ink2">A pagar</p>
                    <p className="font-heading text-xl sm:text-2xl font-black">{brl(g.total)}</p>
                    <p className="text-[11px] text-brand-ink2">{g.orders_count} item(ns)</p>
                  </div>
                </div>

                {g.pix_key ? (
                  <div className="px-4 py-3 bg-brand-yellow border-b-2 border-brand-ink">
                    <div className="flex flex-col md:flex-row gap-4">
                      {g.pix_brcode && (
                        <div className="shrink-0 mx-auto md:mx-0">
                          <div
                            data-testid={`pending-qr-${g.live_id}`}
                            className="bg-white p-2 border-2 border-brand-ink w-fit cursor-pointer"
                            onClick={() => copy(g.pix_brcode, "Código Copia e Cola")}
                            title="Clique para copiar o código Copia e Cola"
                          >
                            <QRCodeSVG value={g.pix_brcode} size={148} level="M" />
                          </div>
                          <p className="text-[10px] text-brand-ink2 text-center mt-1 max-w-[148px]">
                            QR já com <strong>{brl(g.total)}</strong> — escaneie no banco e confirme em 1 toque.
                          </p>
                        </div>
                      )}

                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="badge-brutal !bg-white">Pix · {PIX_TYPE_LABEL[g.pix_type] || (g.pix_type ? g.pix_type.toUpperCase() : "Chave")}</span>
                          {g.pix_name && (
                            <span className="text-xs text-brand-ink2">Favorecido: <strong>{g.pix_name}</strong></span>
                          )}
                        </div>
                        <button
                          type="button"
                          data-testid={`pending-pix-copy-${g.live_id}`}
                          onClick={() => copy(g.pix_key, "Chave Pix")}
                          className="font-mono text-sm px-2 py-1 border-2 border-brand-ink bg-white hover:bg-brand-mint break-all text-left w-full"
                          title="Clique para copiar a chave"
                        >
                          {g.pix_key}
                        </button>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => copy(g.pix_key, "Chave Pix")}
                            className="btn-white !h-9 text-xs"
                          >
                            <Copy weight="bold" size={14} /> Copiar chave
                          </button>
                          {g.pix_brcode && (
                            <button
                              type="button"
                              onClick={() => copy(g.pix_brcode, "Código Copia e Cola")}
                              className="btn-white !h-9 text-xs"
                            >
                              <Copy weight="bold" size={14} /> Copia e cola
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid={`pending-upload-${g.live_id}`}
                            onClick={() => setUploadFor(g)}
                            className="btn-pink !h-9 text-xs"
                          >
                            <PaperPlaneTilt weight="bold" size={14} /> Enviar comprovante
                          </button>
                          {(!g.link_requests || !g.link_requests.some(r => r.status === "pending")) && (
                            <button
                              type="button"
                              data-testid={`pending-link-req-${g.live_id}`}
                              onClick={() => requestLink(g)}
                              disabled={requesting === g.live_id}
                              className="btn-white !h-9 text-xs !bg-brand-purple !text-white"
                              title="Pedir para a loja gerar um link de pagamento"
                            >
                              <LinkSimple weight="bold" size={14} />
                              {requesting === g.live_id ? "Enviando…" : "Solicitar link de pagamento"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-3 bg-brand-cream border-b-2 border-brand-ink text-sm text-brand-ink2 flex flex-wrap items-center gap-2 justify-between">
                    <span>A loja ainda não cadastrou o Pix desta live — combine no direct.</span>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid={`pending-upload-nopix-${g.live_id}`}
                        onClick={() => setUploadFor(g)}
                        className="btn-pink !h-9 text-xs"
                      >
                        <PaperPlaneTilt weight="bold" size={14} /> Enviar comprovante
                      </button>
                      {(!g.link_requests || !g.link_requests.some(r => r.status === "pending")) && (
                        <button
                          type="button"
                          data-testid={`pending-link-req-nopix-${g.live_id}`}
                          onClick={() => requestLink(g)}
                          disabled={requesting === g.live_id}
                          className="btn-white !h-9 text-xs !bg-brand-purple !text-white"
                        >
                          <LinkSimple weight="bold" size={14} /> Solicitar link
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {g.link_requests?.length > 0 && (
                  <div className="px-4 py-3 bg-brand-purple text-white border-b-2 border-brand-ink">
                    <p className="label-brutal !text-[10px] mb-2 !text-white">Link de pagamento</p>
                    <div className="space-y-2">
                      {g.link_requests.map(r => (
                        <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                          <LinkSimple weight="bold" size={14} />
                          <span className="text-[11px]">{new Date(r.created_at).toLocaleString("pt-BR")}</span>
                          {r.amount != null && <strong className="text-[13px]">{brl(r.amount)}</strong>}
                          <span className={`badge-brutal !text-[10px] ${
                            r.status === "sent" ? "!bg-brand-success text-white" :
                            r.status === "canceled" ? "!bg-brand-peach !text-brand-ink" : "!bg-brand-yellow !text-brand-ink"
                          }`}>
                            {r.status === "sent" ? "Enviado no direct" : r.status === "canceled" ? "Cancelado" : "Aguardando a loja gerar"}
                          </span>
                          {r.status === "pending" && (
                            <button
                              type="button"
                              data-testid={`pending-link-cancel-${r.id}`}
                              onClick={() => cancelLinkRequest(r.id)}
                              className="text-[11px] underline hover:text-brand-yellow ml-auto"
                            >
                              cancelar
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] mt-2 opacity-80">
                      A loja irá gerar o link e enviar pra você no direct do Instagram.
                    </p>
                  </div>
                )}

                {g.receipts?.length > 0 && (
                  <div className="px-4 py-3 bg-white border-b-2 border-brand-ink">
                    <p className="label-brutal !text-[10px] mb-2">Comprovantes enviados</p>
                    <div className="flex flex-wrap gap-2">
                      {g.receipts.map(r => {
                        const s = RECEIPT_STATUS[r.status] || RECEIPT_STATUS.submitted;
                        const Icon = s.icon;
                        return (
                          <div key={r.id} className="flex items-center gap-2 border-2 border-brand-ink p-1 pr-2 bg-brand-cream">
                            <button
                              type="button"
                              onClick={() => setLightbox(r.file_url)}
                              className="w-10 h-10 border-2 border-brand-ink bg-white overflow-hidden hover:ring-2 hover:ring-brand-pink"
                              title="Ver comprovante"
                            >
                              <img src={r.file_url} alt="Comprovante" className="w-full h-full object-cover" />
                            </button>
                            <div className="text-[11px]">
                              <p>{new Date(r.created_at).toLocaleString("pt-BR")}</p>
                              {r.amount ? <p className="font-bold">{brl(r.amount)}</p> : null}
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border border-brand-ink ${s.bg} ${s.text} font-bold text-[10px] mt-0.5`}>
                                <Icon weight="bold" size={10} /> {s.label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="tbl w-full min-w-[640px]">
                    <thead>
                      <tr>
                        <th className="w-16">Foto</th>
                        <th className="w-24">Código</th>
                        <th>Descrição</th>
                        <th className="w-14 text-right">Qtd</th>
                        <th className="w-28 text-right">Preço</th>
                        <th className="w-28 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.orders.map(o => (
                        <tr key={o.id} data-testid={`pending-order-${o.id}`}>
                          <td className="text-center">
                            {o.photo_url ? (
                              <button
                                type="button"
                                onClick={() => setLightbox(o.photo_url)}
                                className="w-12 h-12 border-2 border-brand-ink bg-white overflow-hidden inline-flex items-center justify-center hover:ring-2 hover:ring-brand-pink"
                              >
                                <img src={o.photo_url} alt={o.description || "peça"} className="w-full h-full object-cover" />
                              </button>
                            ) : (
                              <span className="w-12 h-12 border-2 border-dashed border-brand-ink2 inline-flex items-center justify-center text-brand-ink2">
                                <ImageIcon weight="bold" size={16} />
                              </span>
                            )}
                          </td>
                          <td className="font-bold">{o.product_code || "—"}</td>
                          <td>{o.description || "—"}</td>
                          <td className="text-right">{o.quantity || 1}</td>
                          <td className="text-right">{brl(o.price)}</td>
                          <td className="text-right font-bold">{brl(o.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {uploadFor && (
        <ReceiptModal
          group={uploadFor}
          onClose={() => setUploadFor(null)}
          onSaved={() => { setUploadFor(null); load(); }}
        />
      )}

      {lightbox && (
        <div
          data-testid="pending-lightbox"
          className="fixed inset-0 bg-brand-ink/80 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute -top-3 -right-3 bg-white border-2 border-brand-ink p-2 z-10"
            >
              <X weight="bold" size={16} />
            </button>
            <img src={lightbox} alt="" className="max-w-[92vw] max-h-[85vh] object-contain border-4 border-brand-ink bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, bg, icon: Icon }) {
  return (
    <div className={`card-brutal p-3 ${bg} flex items-center gap-3`}>
      <Icon weight="bold" size={22} />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase leading-tight">{label}</p>
        <p className="font-heading text-xl sm:text-2xl font-black break-words">{value}</p>
      </div>
    </div>
  );
}

function ReceiptModal({ group, onClose, onSaved }) {
  const [fileUrl, setFileUrl] = useState("");
  const [amount, setAmount] = useState(String(group.total ?? "").replace(".", ","));
  const [note, setNote] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [saving, setSaving] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const compressImage = (file, maxSize = 1600, quality = 0.85) => new Promise((resolve, reject) => {
    // If it's a PDF, keep as-is (base64 encoding)
    if (file.type === "application/pdf") {
      const rd = new FileReader();
      rd.onload = () => resolve(rd.result);
      rd.onerror = reject;
      rd.readAsDataURL(file);
      return;
    }
    const rd = new FileReader();
    rd.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * ratio); height = Math.round(height * ratio);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = rd.result;
    };
    rd.onerror = reject;
    rd.readAsDataURL(file);
  });

  const pick = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      setFileUrl(dataUrl);
    } catch { toast.error("Falha ao ler o arquivo"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!fileUrl) return toast.error("Selecione o arquivo do comprovante");
    const parsed = amount ? parseFloat(String(amount).replace(/\./g, "").replace(",", ".")) : null;
    if (amount && (isNaN(parsed) || parsed <= 0)) return toast.error("Valor inválido");
    setSaving(true);
    try {
      await api.post(`/me/pending-lives/${group.live_id}/receipts`, {
        file_url: fileUrl,
        amount: parsed,
        note: note.trim() || null,
        payment_method: paymentMethod,
      });
      toast.success("Comprovante enviado! A loja vai confirmar em breve.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar");
    } finally { setSaving(false); }
  };

  const isPdf = fileUrl.startsWith("data:application/pdf");

  return (
    <div
      data-testid="receipt-modal"
      className="fixed inset-0 bg-brand-ink/60 z-[70] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="card-brutal bg-white w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-3">
          <div>
            <p className="label-brutal">Enviar comprovante</p>
            <h3 className="font-heading text-xl font-black break-words">{group.live_name}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 border-2 border-brand-ink hover:bg-brand-peach">
            <X weight="bold" size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label-brutal">Comprovante (imagem ou PDF) *</label>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
              data-testid="receipt-camera-input" onChange={(e) => pick(e.target.files?.[0])} />
            <input ref={galleryRef} type="file" accept="image/*,application/pdf" className="hidden"
              data-testid="receipt-gallery-input" onChange={(e) => pick(e.target.files?.[0])} />
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => cameraRef.current?.click()} className="btn-white !h-10">
                <Camera weight="bold" size={14} /> Câmera
              </button>
              <button type="button" onClick={() => galleryRef.current?.click()} className="btn-white !h-10">
                <UploadSimple weight="bold" size={14} /> Galeria/Arquivo
              </button>
              {fileUrl && (
                <button type="button" onClick={() => setFileUrl("")} className="btn-white !h-10 !bg-brand-peach">
                  <X weight="bold" size={14} /> Remover
                </button>
              )}
            </div>
            {fileUrl && !isPdf && (
              <img src={fileUrl} alt="preview" className="mt-2 max-h-48 border-2 border-brand-ink object-contain bg-white" />
            )}
            {isPdf && (
              <p className="mt-2 text-sm font-bold border-2 border-brand-ink p-2 bg-brand-cream">
                📄 PDF anexado
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-brutal">Valor pago</label>
              <input data-testid="receipt-amount"
                className="input-brutal" placeholder="0,00"
                value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
            </div>
            <div>
              <label className="label-brutal">Total a pagar</label>
              <div className="input-brutal !bg-brand-cream flex items-center font-bold">{brl(group.total)}</div>
            </div>
          </div>

          <div>
            <label className="label-brutal">Forma de pagamento *</label>
            <div className="flex gap-2">
              <button type="button" data-testid="receipt-method-pix"
                onClick={() => setPaymentMethod("pix")}
                className={`flex-1 h-11 border-2 border-brand-ink font-bold text-sm ${paymentMethod === "pix" ? "bg-brand-mint" : "bg-white hover:bg-brand-cream"}`}
              >Pix</button>
              <button type="button" data-testid="receipt-method-credit"
                onClick={() => setPaymentMethod("credit")}
                className={`flex-1 h-11 border-2 border-brand-ink font-bold text-sm ${paymentMethod === "credit" ? "bg-brand-yellow" : "bg-white hover:bg-brand-cream"}`}
              >Cartão de crédito</button>
            </div>
          </div>

          <div>
            <label className="label-brutal">Observação (opcional)</label>
            <textarea data-testid="receipt-note"
              className="input-brutal min-h-[70px]" rows={2}
              placeholder="Ex: Paguei hoje 15h — obrigada!"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-white">Cancelar</button>
            <button data-testid="receipt-submit" type="submit" disabled={saving} className="btn-pink">
              {saving ? "Enviando…" : "Enviar comprovante"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
